import { useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Activity,
  Plus,
  CheckCircle,
  XCircle,
  Clock,
  Play,
  Edit,
  Pause,
  BarChart3,
  RefreshCw,
  AlertCircle,
  Download,
  Eye,
  Heart,
  BookMarked,
} from 'lucide-react';

// Importar hooks y tipos
import { useEvaluations } from '../hooks';
import { EvaluationRequest, MCPEvaluation, getEvaluationScore } from '../types';
import { EvaluationFiltersComponent } from './EvaluationFilters';
import EvaluationDetailView from './EvaluationDetailView';

// Helper function to format processing time from milliseconds to seconds
function formatProcessingTime(processing_time_ms?: number): string {
  if (!processing_time_ms) return 'N/A';
  const seconds = processing_time_ms / 1000;
  return `${seconds.toFixed(2)}s`;
}

// Tipos de estado para TypeScript
type StatusType =
  | 'completed'
  | 'failed'
  | 'pending'
  | 'in-progress'
  | 'active'
  | 'draft'
  | 'paused'
  | 'training'
  | 'deployed';

export default function EvaluationsTab() {
  // Estados locales para formularios y UI
  const [selectedEvaluation, setSelectedEvaluation] =
    useState<MCPEvaluation | null>(null);
  const [showFullDetailView, setShowFullDetailView] = useState(false);
  const [newEvaluationData, setNewEvaluationData] = useState<
    Partial<EvaluationRequest>
  >({
    text: '',
    context: '',
    rubric_type: 'general',
  });

  // Hooks para datos
  const evaluations = useEvaluations();

  const getStatusBadge = (status: StatusType) => {
    const statusConfig = {
      completed: {
        className: 'bg-green-100 text-green-800 border-green-200',
        icon: <CheckCircle className='w-3 h-3 mr-1' />,
      },
      failed: {
        className: 'bg-red-100 text-red-800 border-red-200',
        icon: <XCircle className='w-3 h-3 mr-1' />,
      },
      pending: {
        className: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        icon: <Clock className='w-3 h-3 mr-1' />,
      },
      'in-progress': {
        className: 'bg-blue-100 text-blue-800 border-blue-200',
        icon: <RefreshCw className='w-3 h-3 mr-1' />,
      },
      active: {
        className: 'bg-blue-100 text-blue-800 border-blue-200',
        icon: <Play className='w-3 h-3 mr-1' />,
      },
      draft: {
        className: 'bg-gray-100 text-gray-800 border-gray-200',
        icon: <Edit className='w-3 h-3 mr-1' />,
      },
      paused: {
        className: 'bg-orange-100 text-orange-800 border-orange-200',
        icon: <Pause className='w-3 h-3 mr-1' />,
      },
      training: {
        className: 'bg-purple-100 text-purple-800 border-purple-200',
        icon: <BarChart3 className='w-3 h-3 mr-1' />,
      },
      deployed: {
        className: 'bg-green-100 text-green-800 border-green-200',
        icon: <CheckCircle className='w-3 h-3 mr-1' />,
      },
    };

    const config = statusConfig[status] || statusConfig.draft;

    return (
      <Badge className={`flex items-center border ${config.className}`}>
        {config.icon}
        {status}
      </Badge>
    );
  };

  const handleCreateEvaluation = async () => {
    if (newEvaluationData.text) {
      const success = await evaluations.createEvaluation(
        newEvaluationData as EvaluationRequest
      );
      if (success) {
        setNewEvaluationData({ text: '', context: '', rubric_type: 'general' });
      }
    }
  };

  // Componente de error
  const ErrorAlert = ({
    error,
    onRetry,
  }: {
    error: any;
    onRetry: () => void;
  }) => (
    <div className='bg-red-50 border border-red-200 rounded-lg p-4 mb-4'>
      <div className='flex items-center'>
        <AlertCircle className='h-4 w-4 text-red-600 mr-2' />
        <span className='text-red-800'>
          {error?.message || 'Ha ocurrido un error'}
        </span>
        <Button
          variant='outline'
          size='sm'
          onClick={onRetry}
          className='ml-auto'
        >
          <RefreshCw className='h-3 w-3 mr-1' />
          Reintentar
        </Button>
      </div>
    </div>
  );

  // Si está en modo vista completa, renderizar solo la vista de detalle
  if (showFullDetailView && selectedEvaluation) {
    return (
      <EvaluationDetailView
        evaluation={selectedEvaluation}
        onBack={() => {
          setShowFullDetailView(false);
          setSelectedEvaluation(null);
        }}
        onToggleLike={async (id: string) => {
          const result = await evaluations.toggleLikeEvaluation(id);
          return result || undefined;
        }}
        onToggleTraining={async (id: string) => {
          const result = await evaluations.toggleTrainingData(id);
          return result || undefined;
        }}
      />
    );
  }

  return (
    <div className='space-y-6'>
      <Card className='border-0 shadow-sm'>
        <CardHeader className='border-b bg-white'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center space-x-3'>
              <div className='flex items-center justify-center w-10 h-10 bg-blue-100 rounded-lg animate-glow'>
                <Activity className='h-5 w-5 text-blue-600' />
              </div>
              <div>
                <CardTitle className='text-lg text-gray-900'>
                  Evaluaciones Registradas
                </CardTitle>
                <CardDescription>
                  Monitoreo de queries ejecutadas en el sistema
                </CardDescription>
              </div>
            </div>
            <div className='flex items-center space-x-2'>
              <Button variant='outline' onClick={evaluations.exportEvaluations}>
                <Download className='h-4 w-4 mr-2' />
                Exportar
              </Button>
              <Dialog>
                <DialogTrigger asChild>
                  <Button className='bg-blue-600 hover:bg-blue-700 text-white'>
                    <Plus className='h-4 w-4 mr-2' />
                    Nueva Evaluación
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Crear Nueva Evaluación</DialogTitle>
                    <DialogDescription>
                      Evalúa un texto usando el sistema de rúbricas
                    </DialogDescription>
                  </DialogHeader>
                  <div className='space-y-4'>
                    <div>
                      <Label htmlFor='eval-text'>Texto a evaluar</Label>
                      <Textarea
                        id='eval-text'
                        value={newEvaluationData.text}
                        onChange={e =>
                          setNewEvaluationData(prev => ({
                            ...prev,
                            text: e.target.value,
                          }))
                        }
                        placeholder='Ingresa el texto que quieres evaluar...'
                        rows={5}
                      />
                    </div>
                    <div>
                      <Label htmlFor='eval-context'>Contexto (opcional)</Label>
                      <Input
                        id='eval-context'
                        value={newEvaluationData.context}
                        onChange={e =>
                          setNewEvaluationData(prev => ({
                            ...prev,
                            context: e.target.value,
                          }))
                        }
                        placeholder='Contexto adicional para la evaluación'
                      />
                    </div>
                    <div>
                      <Label htmlFor='eval-rubric'>Tipo de rúbrica</Label>
                      <Select
                        value={newEvaluationData.rubric_type}
                        onValueChange={value =>
                          setNewEvaluationData(prev => ({
                            ...prev,
                            rubric_type: value,
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value='general'>General</SelectItem>
                          <SelectItem value='comunicacion'>
                            Comunicación
                          </SelectItem>
                          <SelectItem value='ventas'>Ventas</SelectItem>
                          <SelectItem value='analisis'>Análisis</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      className='w-full bg-blue-600 hover:bg-blue-700'
                      onClick={handleCreateEvaluation}
                      disabled={evaluations.loading || !newEvaluationData.text}
                    >
                      {evaluations.loading ? (
                        <RefreshCw className='h-4 w-4 mr-2 animate-spin' />
                      ) : null}
                      Evaluar Texto
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </CardHeader>

        {/* Componente de Filtros Avanzados */}
        <div className='px-6 pt-6'>
          <EvaluationFiltersComponent
            filters={evaluations.filters}
            onFiltersChange={evaluations.updateFilters}
            availableModels={[
              ...new Set(
                evaluations.evaluations
                  .map(e => e.model)
                  .filter((model): model is string => Boolean(model))
              ),
            ]}
          />
        </div>

        <CardContent className='p-0'>
          {evaluations.error && (
            <ErrorAlert
              error={evaluations.error}
              onRetry={evaluations.refreshEvaluations}
            />
          )}

          {evaluations.loading ? (
            <div className='flex justify-center items-center p-8 min-h-[400px]'>
              <RefreshCw className='h-6 w-6 animate-spin text-blue-600' />
              <span className='ml-2 text-gray-600'>
                {Object.keys(evaluations.filters).length > 0
                  ? 'Aplicando filtros...'
                  : 'Cargando evaluaciones...'}
              </span>
            </div>
          ) : (
            <div className='min-h-[600px]'>
              <Table>
                <TableHeader>
                  <TableRow className='bg-gray-50'>
                    <TableHead className='font-semibold text-gray-700'>
                      ID
                    </TableHead>
                    <TableHead className='font-semibold text-gray-700'>
                      Query
                    </TableHead>
                    <TableHead className='font-semibold text-gray-700'>
                      Timestamp
                    </TableHead>
                    <TableHead className='font-semibold text-gray-700'>
                      Estado
                    </TableHead>
                    <TableHead className='font-semibold text-gray-700'>
                      Duración
                    </TableHead>
                    <TableHead className='font-semibold text-gray-700'>
                      Puntuación
                    </TableHead>
                    <TableHead className='font-semibold text-gray-700'>
                      Respuesta
                    </TableHead>
                    <TableHead className='font-semibold text-gray-700'>
                      Acciones
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {evaluations.evaluations.map((evaluation, index) => (
                    <TableRow
                      key={evaluation.id}
                      className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}
                    >
                      <TableCell className='font-mono text-sm font-medium text-blue-600'>
                        <div className='flex items-center gap-2'>
                          {evaluation.id}
                          <div className='flex gap-1'>
                            {evaluation.is_liked && (
                              <Heart className='h-3 w-3 text-pink-600 fill-current' />
                            )}
                            {evaluation.is_training_data && (
                              <BookMarked className='h-3 w-3 text-purple-600 fill-current' />
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className='max-w-xs truncate font-medium text-gray-900'>
                        {evaluation.query}
                      </TableCell>
                      <TableCell className='text-sm text-gray-600'>
                        {new Date(evaluation.timestamp).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(evaluation.status as StatusType)}
                      </TableCell>
                      <TableCell className='font-mono font-medium text-gray-700'>
                        {formatProcessingTime(evaluation.processing_time_ms)}
                      </TableCell>
                      <TableCell>
                        {getEvaluationScore(evaluation) !== undefined ? (
                          <div className='flex items-center gap-1'>
                            <Badge
                              className={`${getEvaluationScore(evaluation)! >= 8
                                ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                                : 'bg-green-100 text-green-800'
                                }`}
                            >
                              {getEvaluationScore(evaluation)!.toFixed(1)}
                            </Badge>
                            {getEvaluationScore(evaluation)! >= 8 && (
                              <Badge className='bg-yellow-100 text-yellow-800 text-xs px-1'>
                                AUTO
                              </Badge>
                            )}
                          </div>
                        ) : (
                          <span className='text-gray-400'>-</span>
                        )}
                      </TableCell>
                      <TableCell className='max-w-xs truncate text-gray-600'>
                        {evaluation.response}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant='outline'
                          size='sm'
                          onClick={() => {
                            setSelectedEvaluation(evaluation);
                            setShowFullDetailView(true);
                          }}
                          className='text-blue-600 hover:text-blue-700'
                        >
                          <Eye className='h-4 w-4 mr-1' />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {evaluations.evaluations.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        className='text-center text-gray-500 py-8'
                      >
                        No hay evaluaciones disponibles
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de Detalle de Evaluación */}
      <Dialog
        open={!!selectedEvaluation && !showFullDetailView}
        onOpenChange={open => !open && setSelectedEvaluation(null)}
      >
        <DialogContent className='max-w-4xl max-h-[80vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle className='flex items-center gap-2'>
              <Eye className='h-5 w-5 text-blue-600' />
              Detalle de Evaluación - {selectedEvaluation?.id}
            </DialogTitle>
            <DialogDescription>
              Información completa de la evaluación realizada
            </DialogDescription>
          </DialogHeader>

          {selectedEvaluation && (
            <div className='space-y-6'>
              {/* Header con información principal */}
              <div className='grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg'>
                <div>
                  <Label className='text-sm font-semibold text-gray-700'>
                    ID
                  </Label>
                  <p className='font-mono text-blue-600 font-medium'>
                    {selectedEvaluation.id}
                  </p>
                </div>
                <div>
                  <Label className='text-sm font-semibold text-gray-700'>
                    Timestamp
                  </Label>
                  <p className='text-gray-900'>
                    {new Date(selectedEvaluation.timestamp).toLocaleString(
                      'es-ES'
                    )}
                  </p>
                </div>
                <div>
                  <Label className='text-sm font-semibold text-gray-700'>
                    Estado
                  </Label>
                  <div className='mt-1'>
                    {getStatusBadge(selectedEvaluation.status as StatusType)}
                  </div>
                </div>
                <div>
                  <Label className='text-sm font-semibold text-gray-700'>
                    Duración
                  </Label>
                  <p className='font-mono text-gray-900 font-medium'>
                    {formatProcessingTime(selectedEvaluation.processing_time_ms)}
                  </p>
                </div>
              </div>

              {/* Información adicional */}
              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <Label className='text-sm font-semibold text-gray-700'>
                    Puntuación
                  </Label>
                  <div className='mt-1'>
                    {getEvaluationScore(selectedEvaluation) !== undefined ? (
                      <Badge className='bg-green-100 text-green-800 text-lg px-3 py-1'>
                        {getEvaluationScore(selectedEvaluation)!.toFixed(1)}
                      </Badge>
                    ) : (
                      <span className='text-gray-400'>Sin puntuación</span>
                    )}
                  </div>
                </div>
                <div>
                  <Label className='text-sm font-semibold text-gray-700'>
                    Modelo
                  </Label>
                  <p className='text-gray-900'>
                    {selectedEvaluation.model || 'No especificado'}
                  </p>
                </div>
                <div>
                  <Label className='text-sm font-semibold text-gray-700'>
                    Tipo de Rúbrica
                  </Label>
                  <p className='text-gray-900'>
                    {selectedEvaluation.rubric_type || 'General'}
                  </p>
                </div>
                <div>
                  <Label className='text-sm font-semibold text-gray-700'>
                    Estado de Entrenamiento
                  </Label>
                  <div className='flex gap-2 mt-1 items-center'>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={async () => {
                        const updated = await evaluations.toggleLikeEvaluation(
                          selectedEvaluation.id
                        );
                        if (updated) {
                          setSelectedEvaluation(updated);
                        }
                      }}
                      className={`${selectedEvaluation.is_liked
                        ? 'bg-pink-100 text-pink-700 border-pink-300 hover:bg-pink-200'
                        : 'text-gray-600 hover:text-pink-600'
                        }`}
                    >
                      <Heart
                        className={`h-4 w-4 mr-1 ${selectedEvaluation.is_liked ? 'fill-current' : ''}`}
                      />
                      {selectedEvaluation.is_liked ? 'Liked' : 'Like'}
                    </Button>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={async () => {
                        const updated = await evaluations.toggleTrainingData(
                          selectedEvaluation.id
                        );
                        if (updated) {
                          setSelectedEvaluation(updated);
                        }
                      }}
                      className={`${selectedEvaluation.is_training_data
                        ? 'bg-purple-100 text-purple-700 border-purple-300 hover:bg-purple-200'
                        : 'text-gray-600 hover:text-purple-600'
                        }`}
                    >
                      <BookMarked
                        className={`h-4 w-4 mr-1 ${selectedEvaluation.is_training_data ? 'fill-current' : ''}`}
                      />
                      {selectedEvaluation.is_training_data
                        ? 'En Entrenamiento'
                        : 'Usar para Entrenamiento'}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Query */}
              <div>
                <Label className='text-sm font-semibold text-gray-700'>
                  Query / Consulta
                </Label>
                <div className='mt-2 p-4 bg-blue-50 border border-blue-200 rounded-lg'>
                  <p className='text-gray-900 whitespace-pre-wrap'>
                    {selectedEvaluation.query}
                  </p>
                </div>
              </div>

              {/* Response */}
              <div>
                <Label className='text-sm font-semibold text-gray-700'>
                  Respuesta Generada
                </Label>
                <div className='mt-2 p-4 bg-green-50 border border-green-200 rounded-lg'>
                  <p className='text-gray-900 whitespace-pre-wrap'>
                    {selectedEvaluation.response}
                  </p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
